import { countFace } from '../../shared/rules';
import type { ClientView, Face, PublicPlayer } from '../../shared/types';
import { die, el } from '../dom';

/** Roster: name, dice count, and who is on the clock. Never other people's faces. */
export function renderPlayers(view: ClientView): HTMLElement {
  const list = el('ul', { class: 'players', 'aria-label': 'Players' });

  for (const player of [...view.players].sort((a, b) => a.seat - b.seat)) {
    list.appendChild(renderPlayer(view, player));
  }
  return list;
}

function renderPlayer(view: ClientView, player: PublicPlayer): HTMLElement {
  const isActive = view.activePlayerId === player.id;
  const isYou = view.you.id === player.id;

  const classes = ['player'];
  if (isActive) classes.push('player--active');
  if (player.eliminated) classes.push('player--out');
  if (!player.connected) classes.push('player--away');

  const revealed = view.lastReveal?.allDice[player.id];

  const dice = el('div', { class: 'player__dice' });
  if (view.phase === 'lobby') {
    // Nothing has been rolled yet, so showing five face-down cups would imply a
    // hand that does not exist.
  } else if (revealed !== undefined) {
    // Reveal phase: everyone's dice, highlighted against the challenged bid.
    const face = view.lastReveal?.bid.face;
    for (const value of revealed) {
      const state =
        face === undefined
          ? 'plain'
          : value === face
            ? 'match'
            : value === 1 && face !== 1
              ? 'wild'
              : 'plain';
      dice.appendChild(die(value, state));
    }
  } else if (isYou) {
    for (const value of view.you.dice) dice.appendChild(die(value, 'own'));
  } else {
    // Face-down cups. These faces were never sent to this client at all.
    for (let i = 0; i < player.diceCount; i += 1) {
      dice.appendChild(die(null, 'hidden'));
    }
  }

  const badges = el('span', { class: 'player__badges' });
  if (player.id === view.hostId) {
    badges.appendChild(el('span', { class: 'badge', text: 'host' }));
  }
  if (!player.connected) {
    badges.appendChild(el('span', { class: 'badge badge--away', text: 'away' }));
  }
  if (player.eliminated) {
    badges.appendChild(el('span', { class: 'badge badge--out', text: 'out' }));
  }

  return el('li', { class: classes.join(' ') }, [
    el('div', { class: 'player__head' }, [
      el('span', {
        class: 'player__name',
        // textContent — the name comes from another player.
        text: isYou ? `${player.name} (you)` : player.name,
      }),
      badges,
      el('span', {
        class: 'player__count',
        text:
          view.phase === 'lobby'
            ? player.ready
              ? 'ready'
              : 'not ready'
            : `${player.diceCount} ${player.diceCount === 1 ? 'die' : 'dice'}`,
      }),
    ]),
    dice,
  ]);
}

/** Current bid plus the round's history, most recent first. */
export function renderBidHistory(view: ClientView): HTMLElement {
  const names = new Map(view.players.map((p) => [p.id, p.name]));
  const section = el('section', { class: 'history', 'aria-label': 'Bids this round' });

  if (view.currentBid === null) {
    section.appendChild(
      el('p', { class: 'history__empty', text: 'No bid yet this round.' }),
    );
    return section;
  }

  section.appendChild(
    el('p', { class: 'history__current' }, [
      el('span', { text: `${names.get(view.currentBid.playerId) ?? 'Someone'} bid ` }),
      el('strong', {
        text: `${view.currentBid.quantity} × ${view.currentBid.face}${
          view.currentBid.face === 1 ? ' (wild)' : 's'
        }`,
      }),
    ]),
  );

  if (view.bidHistory.length > 1) {
    const list = el('ol', { class: 'history__list' });
    for (const bid of [...view.bidHistory].reverse().slice(1)) {
      list.appendChild(
        el('li', {
          text: `${names.get(bid.playerId) ?? 'Someone'}: ${bid.quantity} × ${bid.face}s`,
        }),
      );
    }
    section.appendChild(list);
  }

  return section;
}

/**
 * Reveal panel: what was claimed, what was actually there, and who paid.
 * §12 asks for the wild 1s to be called out explicitly, because "4 fives" being
 * satisfied by two 1s is the part newcomers find surprising.
 */
/**
 * Which reveal we have already played the entrance animation for.
 *
 * Every broadcast rebuilds the whole tree, so the reveal node is a *new* element
 * each time — and a CSS entrance animation restarts from its first frame on each
 * one. Anything happening during a reveal (a player disconnecting, say) would
 * otherwise reset it to opacity 0 and leave the panel flickering or invisible.
 * Animate only when the reveal itself is genuinely new.
 */
let animatedRevealKey: string | null = null;

export function renderReveal(view: ClientView): HTMLElement | null {
  const reveal = view.lastReveal;
  if (reveal === null) {
    animatedRevealKey = null;
    return null;
  }

  const names = new Map(view.players.map((p) => [p.id, p.name]));
  const allDice = Object.values(reveal.allDice).flat() as Face[];
  const wilds = reveal.bid.face === 1 ? 0 : countFace(allDice, 1, false);
  const exact = countFace(allDice, reveal.bid.face, false);

  const held = reveal.actual >= reveal.bid.quantity;
  const loser = names.get(reveal.loserId) ?? 'Someone';

  const summary = el('div', { class: 'reveal__summary' }, [
    el('p', { class: 'reveal__claim' }, [
      el('span', {
        text: `${names.get(reveal.bid.playerId) ?? 'Someone'} claimed `,
      }),
      el('strong', { text: `${reveal.bid.quantity} × ${reveal.bid.face}s` }),
    ]),
    el('p', { class: `reveal__actual ${held ? 'is-good' : 'is-bad'}` }, [
      el('strong', { text: String(reveal.actual) }),
      el('span', {
        text: ` actually on the table — ${held ? 'the bid held' : 'not enough'}`,
      }),
    ]),
  ]);

  if (wilds > 0) {
    summary.appendChild(
      el('p', {
        class: 'reveal__wilds',
        text: `${exact} showing ${reveal.bid.face}, plus ${wilds} wild ${
          wilds === 1 ? 'one' : 'ones'
        }.`,
      }),
    );
  }

  summary.appendChild(
    el('p', { class: 'reveal__loser', text: `${loser} loses a die.` }),
  );

  const key = `${view.roundNumber}:${reveal.loserId}:${reveal.actual}`;
  const isNew = key !== animatedRevealKey;
  animatedRevealKey = key;

  return el('section', {
    class: `reveal ${isNew ? 'reveal--enter' : ''}`,
    'aria-label': 'Reveal',
    // Announced to screen readers the moment it appears.
    'aria-live': 'assertive',
  }, [summary]);
}
